import { useSelector } from 'react-redux';
import { isNil } from 'lodash';
import {
  fullFerrySelfServiceEnabled,
  getReservationsByProductType,
  removeDuplicatedVehicle,
} from 'utils/helpers';
import {
  isBookingFromSysAdmin,
  isReservationUnchecked,
  isSimpleBooking,
} from 'utils/helpers/booking';
import { getCurrentBooking } from 'redux/selectors/current_booking';

export const isVehiclesTooLong = ({
  vehiclesAttributes = [],
  cmsVehicleTypes = [],
  maxCarLength,
  maxVehicleLength,
}) => {
  // First remove vehicles with same registration (because of truck)
  const vehicles = removeDuplicatedVehicle(vehiclesAttributes);
  // Then get all non-trailer vehicles and get their max length.
  const vehicleInfoGroups = vehicles
    .filter(({ linked_to }) => isNil(linked_to))
    .reduce((acc, item) => {
      const { id, vehicle_type_id: qtVehicleTypeId } = item;
      const cmsVehicleType = cmsVehicleTypes.find(
        ({ qt_vehicle_type_id }) => qt_vehicle_type_id === qtVehicleTypeId
      );
      const maxLengthFromVehicleType =
        cmsVehicleType?.limitOnDimensions?.maxLength;
      acc[id] = {
        length: Number(item.length),
        maxLength: maxLengthFromVehicleType || maxCarLength,
      };
      return acc;
    }, {});
  const trailers = vehicles.filter(({ linked_to }) => !isNil(linked_to));
  trailers.forEach(({ length, linked_to }) => {
    vehicleInfoGroups[linked_to].length += Number(length);
    vehicleInfoGroups[linked_to].maxLength = maxVehicleLength;
  });
  let reachedMaxVehicleLength = false;
  let maxLengthInUse = 0;
  Object.values(vehicleInfoGroups).some(({ length, maxLength }) => {
    reachedMaxVehicleLength = length > maxLength;

    if (reachedMaxVehicleLength) {
      maxLengthInUse = maxLength;
    }
    return reachedMaxVehicleLength;
  });
  return {
    reachedMaxVehicleLength,
    maxLengthInUse,
  };
};

export const useCurrentBookingState = () => {
  const currentBooking = useSelector(getCurrentBooking);
  const cmsVehicleTypes = useSelector(
    (state) => state.sealink?.vehicle_types || []
  );
  const {
    maximum_vehicle_length: maxVehicleLength,
    maximum_car_length: maxCarLength,
  } = useSelector((state) => state.sealink?.vehicle_config || {});
  const luggageTypes = useSelector(
    (state) => state.sealink?.luggage?.luggageTypes || []
  );
  const allowedVehicleTypes = cmsVehicleTypes
    .map((vehicleType) => vehicleType.qt_vehicle_type_id)
    .concat(
      Object.values(luggageTypes).map(
        (luggageType) => luggageType.qtLuggageTypeId
      )
    );

  const isDraft = Boolean(currentBooking.booking_a_id);
  const isNewBooking =
    currentBooking.state === 'new' || currentBooking.state === undefined;

  const isEditing = !isNewBooking || isDraft;
  const isActive = currentBooking.state === 'active';
  const isCancelled = currentBooking.state === 'cancelled';

  const isBookingPayable =
    currentBooking && currentBooking?.reservation_ids?.length > 0 && !isDraft;

  const derivedSimpleBooking = currentBooking
    ? isSimpleBooking(
        currentBooking,
        allowedVehicleTypes,
        fullFerrySelfServiceEnabled()
      )
    : undefined;

  const reservations = getReservationsByProductType('ferry', currentBooking);
  const { reachedMaxVehicleLength, maxLengthInUse } = isVehiclesTooLong({
    vehiclesAttributes: currentBooking.vehicles_attributes,
    cmsVehicleTypes,
    maxCarLength,
    maxVehicleLength,
  });

  // If it is from sys admin, we assume it is respax booking, so cannot modify
  const fromSysAdmin = isBookingFromSysAdmin(currentBooking);
  const isReturnBooking = derivedSimpleBooking && reservations.length === 2;
  const canEditDepartureReservation =
    derivedSimpleBooking &&
    reservations[0] &&
    !reservations[0].time_restricted &&
    isReservationUnchecked(reservations[0]);

  const canEditReturnReservation =
    isReturnBooking &&
    !reservations[1].time_restricted &&
    isReservationUnchecked(reservations[1]);

  const canEditBooking =
    (isNewBooking ||
      (!isCancelled &&
        (canEditDepartureReservation || canEditReturnReservation))) &&
    !fromSysAdmin &&
    !reachedMaxVehicleLength;

  const canEditReturnOnly = canEditBooking && !canEditDepartureReservation;

  const isFullyPaid =
    currentBooking.balance_in_cents <= 0 && currentBooking.state === 'active';

  return {
    isNewBooking,
    isEditing,
    isActive,
    isCancelled,
    isDraft,
    isSimpleBooking: derivedSimpleBooking,
    canEditBooking,
    canEditReturnOnly,
    isBookingPayable,
    reachedMaxVehicleLength,
    maxLength: maxLengthInUse,
    isFullyPaid,
  };
};
